import React, { useEffect, useState } from "react";
import {
	Box,
	Button,
	Drawer,
	DrawerBody, DrawerCloseButton,
	DrawerContent,
	DrawerOverlay,
	useDisclosure,
} from "~/foundation/Chakra";
import { ChatBot, type ChatBotProps } from "./ChatBot";
import { Icon } from "../Icon";
import { useTranslation } from "~/foundation/Dictionary";
import { getChatHistoryCookie } from "./cookieHandler";
import type { SearchDictionary } from "~/feature/Search/dictionary.ts"; // eslint-disable-line
import type { AliaChatMessage } from "~/foundation/SitecoreContent/generated-types";

export type MessageStatus = "in" | "out" | "error";

export type ChatMessage = AliaChatMessage & { status: MessageStatus };

type ChatBotDrawerProps = ChatBotProps & {
	label?: string;
	collapsed?: boolean;
}

export function ChatBotDrawer({ label, isRtl, collapsed = false, ...props }: ChatBotDrawerProps) {
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [t] = useTranslation<SearchDictionary>();
	const [chatHistory, setChatHistory] = useState<string[]>([]);

	useEffect(() => {
		if (props.articleId) {
			setChatHistory(getChatHistoryCookie()[props.articleId]);
		}
	}, [props.articleId]);

	return (
		<>
			<Button
				onClick={onOpen}
				h="fit-content"
				fontSize="xs"
				bg={label ? "primary.darkBlue" : "transparent"}
				textColor={label ? "primary.white" : undefined}
				px={label ? (collapsed ? 0 : 3) : 0}
				py={label ? (collapsed ? 1 : 1) : 0}
				minW={label ? "44px" : undefined}
				borderRadius="full"
				borderColor="primary.aramcoBlue"
				borderStyle="solid"
				borderWidth={label ? 0 : "2px"}
				transitionProperty="background-color, border-color, border-width, color, padding, margin"
				transitionDuration="0.28s"
				transitionTimingFunction="ease"
				aria-label={t("search.askAlia")}
				_hover={label ? {
					color: "white"
				} : {
					color: "white",
					borderColor: "primary.aramcoLinkBlue",
				}}
				flexShrink={0}
			>
				<Box
					borderRadius="full"
					ms={label ? collapsed ? 0 : 2 : 0}
					me={label ? collapsed ? 0 : 2 : 0}
					transitionProperty="color, width, height, margin"
					transitionDuration="0.28s"
					transitionTimingFunction="ease"
					w={label ? 9 : 8}
					h={label ? 9 : 8}
					position="relative"
					className="alia-icon"
					_before={label ? {
						content: '""',
						borderRadius: "full",
						display: "block",
						position: "absolute",
						bgColor: "primary.darkBlue",
						w: "100%",
						h: "100%",
						left: 0,
						right: 0,
						transition: "transform 500ms ease"
					} : {}}
				>
					<Icon
						color={label ? "white" : "primary.aramcoBlue"}
						variant="Alia"
						w="100%"
						position="relative"
						zIndex={1}
					/>
				</Box>

				{/* History indicator. Only for elements articles */}
				{chatHistory && chatHistory.length > 0 && (
					<Box
						position="absolute"
						bgColor="secondary.red100"
						rounded="full"
						w="10px"
						h="10px"
						{...(label ?
							(collapsed ? {
								top: "0.5rem",
								left: "2.25rem"
							} : (isRtl ? {
								top: "0.5rem",
								right: "1rem"
							} : {
								top: "0.5rem",
								left: "3rem"
							})) : {
								top: "-0.1rem",
								right: "0"
							})}
					/>
				)}

				{label && (
					<Box
						as="span"
						zIndex={1}
						position="relative"
						display="inline-block"
						overflow="hidden"
						whiteSpace="nowrap"
						verticalAlign="middle"
						flexShrink={1}
						minW={0}
						maxW={collapsed ? 0 : "15rem"}
						opacity={collapsed ? 0 : 1}
						pe={collapsed ? 0 : 3}
						pointerEvents={collapsed ? "none" : "auto"}
						transitionProperty="max-width, opacity, padding-inline-end"
						transitionDuration="0.28s"
						transitionTimingFunction="ease"
						aria-hidden={collapsed}
					>
						{label}
					</Box>
				)}
			</Button>
			<Drawer placement={"end"} onClose={onClose} isOpen={isOpen}>
				<DrawerOverlay bgColor="blackAlpha.700">
					<DrawerContent maxW="container.md" maxH="100%">
						<DrawerBody position="relative" h="100%">
							<Box
								position="absolute"
								top={{ base: 4, md: "50%" }}
								insetStart={{ base: "unset", md: 0 }}
								insetEnd={{ base: 4, md: "unset" }}
								bgColor="primary.darkBlue"
								roundedStart="full"
								p={[".5rem", null, null, "1rem"]}
								color="white"
								transform={{ base: "none", md: `translateY(-50%) translateX(${isRtl ? "100%" : "-100%"})` }}>
								<DrawerCloseButton
									position="relative"
									zIndex={2}
									border="2px solid"
									p={2}
									borderRadius="full"
									transition="transform 250ms ease"
									w={["2.1875rem", null, null, "2.8125rem"]}
									h={["2.1875rem", null, null, "2.8125rem"]}
									_hover={{
										transform: [null, null, null, "rotate(45deg)"]
									}}
									sx={{
										"svg": {
											w: [".75rem", null, null, "1rem"],
											h: [".75rem", null, null, "1rem"]
										},
										willChange: "transform",
										backfaceVisibility: "hidden",
									}}
								/>
							</Box>
							<ChatBot
								{...props}
								isRtl={isRtl}
								onHistoryChange={() => {
									if (props.articleId) {
										setChatHistory(getChatHistoryCookie()[props.articleId]);
									}
								}}
							/>
						</DrawerBody>
					</DrawerContent>
				</DrawerOverlay>
			</Drawer>
		</>
	)
}
